// src/components/ui/CommentThread.jsx
// Round 85 — file rewritten from scratch in clean UTF-8.
//
// Previous version had triple-encoded mojibake ("comentário" rendered as
// "comentÃƒÆ’Ã‚Â¡rio", "há 18h" as "hÃƒÆ’Ã‚Â¡ 18h") caused by an older Python
// edit that read the file as latin-1, mutated, then re-wrote — three times.
// The page still rendered, but every accented character in the JSX strings
// came out as a tofu square.
//
// Round 85 keeps the v18 behaviour (avatar + name buttons in CommentRow
// trigger `onUserClick` for navigation to /profile/<username>) and writes
// every string literal in proper UTF-8.
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, Trash2, Send, Reply, ChevronDown, ChevronUp, MessageCircle, X,
} from 'lucide-react';
import Avatar from './Avatar';
import { COMMENT_LIMITS } from '../../config/commentConfig';
import './CommentThread.css';

/* ──────────── Helpers (exported for callers) ──────────── */

// Take a tree (comment.replies, comment.replies.replies, …) and
// return a flat list of comments ordered parent-first. Each comment
// gets a `__depth` so the row can indent replies.
export function flattenCommentTree(comments = []) {
  const flat = [];
  const walk = (list, depth = 0, parentId = null) => {
    if (!Array.isArray(list)) return;
    for (const c of list) {
      if (!c) continue;
      const effectiveParentId = c.parentId ?? c.parentCommentId ?? parentId;
      flat.push({ ...c, parentId: effectiveParentId, __depth: depth });
      if (Array.isArray(c.replies) && c.replies.length > 0) {
        walk(c.replies, depth + 1, c.id ?? c.commentId ?? null);
      }
    }
  };
  walk(comments);
  return flat;
}

// "há 18h" / "há 5 min" / "há 3d" formatter (PT-PT, lowercase). Used as
// a compact relative timestamp next to the absolute one.
function timeAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diff < 60) return `há ${diff}s`;
  const min = Math.floor(diff / 60);
  if (min < 60) return `há ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `há ${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `há ${day}d`;
  return new Date(iso).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' });
}

// "07/08/2026 18:42" absolute timestamp. Hover the relative label to
// see this. Stable across timezones because we read the ISO string as-is.
function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-PT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// Home and trip-detail comments arrive as a flat list with `parentId`, while
// the forum keeps the same relationship nested under `replies`. Preserve the
// existing nested shape and rebuild flat lists into a parent-first order so a
// newly submitted reply is rendered immediately below the comment it answers.
function orderFlatThread(comments) {
  const source = Array.isArray(comments) ? comments : [];
  if (!source.some((comment) => comment?.parentId != null)) return source;

  const byId = new Map();
  const children = new Map();
  const roots = [];
  const sourceIndex = new Map();

  source.forEach((comment, index) => {
    if (comment?.id != null) {
      byId.set(String(comment.id), comment);
      sourceIndex.set(String(comment.id), index);
    }
  });

  source.forEach((comment) => {
    if (!comment) return;
    const parentKey = comment.parentId != null ? String(comment.parentId) : null;
    if (parentKey && byId.has(parentKey)) {
      const list = children.get(parentKey) || [];
      list.push(comment);
      children.set(parentKey, list);
    } else {
      roots.push(comment);
    }
  });

  const compare = (a, b) => {
    const ta = a?.createdAt ? new Date(a.createdAt).getTime() : Number.NEGATIVE_INFINITY;
    const tb = b?.createdAt ? new Date(b.createdAt).getTime() : Number.NEGATIVE_INFINITY;
    // Newest comments first, consistently with the API and with the
    // notifications feed. Keep the original order only when timestamps tie
    // or are unavailable.
    if (ta !== tb && Number.isFinite(ta) && Number.isFinite(tb)) return tb - ta;
    return (sourceIndex.get(String(a?.id)) || 0) - (sourceIndex.get(String(b?.id)) || 0);
  };

  const ordered = [];
  const visited = new Set();
  const visit = (comment, depth) => {
    if (!comment || comment.id == null || visited.has(String(comment.id))) return;
    const key = String(comment.id);
    visited.add(key);
    ordered.push({ ...comment, __depth: Math.max(Number(comment.__depth) || 0, depth) });
    (children.get(key) || []).sort(compare).forEach((child) => visit(child, depth + 1));
  };

  roots.sort(compare).forEach((comment) => visit(comment, 0));
  // A malformed/partially paginated response must not make comments vanish.
  source.forEach((comment) => {
    if (comment?.id != null && !visited.has(String(comment.id))) visit(comment, 0);
  });
  return ordered;
}

const INITIAL_VISIBLE_ROOTS = 3;
const INITIAL_VISIBLE_REPLIES = 3;
const QUICK_REACTIONS = ['❤️❤️❤️', '😁😁😁', '🥰🥰🥰', '😂😂😂', '😍'];

function getCommentKey(comment) {
  return comment?.id != null
    ? String(comment.id)
    : `${comment?.userId || 'comment'}-${comment?.createdAt || comment?.text || comment?.content || ''}`;
}

function getParentId(comment) {
  return comment?.parentId ?? comment?.parentCommentId ?? null;
}

// Replies are persisted as normal comment text. Add the target handle at the
// moment of submission so the mention is visible in the thread, remains part
// of the notification preview and works consistently on every consumer of
// CommentThread (feed, trip details and forum).
function getMentionHandle(comment) {
  const raw = comment?.username
    || comment?.userName
    || comment?.authorUsername
    || comment?.user
    || '';
  return String(raw)
    .trim()
    .replace(/^@+/, '')
    .replace(/\s+/g, '');
}

function withReplyMention(text, comment) {
  const value = String(text || '').trim();
  if (!value) return value;
  const handle = getMentionHandle(comment);
  if (!handle) return value;
  const escapedHandle = handle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const alreadyMentioned = new RegExp(`^@${escapedHandle}(?:\\s|$)`, 'i').test(value);
  return alreadyMentioned ? value : `@${handle} ${value}`;
}

function renderCommentText(text) {
  const value = String(text || '');
  const match = value.match(/^(@[^\s]+)(\s|$)/);
  if (!match) return value;
  return (
    <>
      <span className="gm-ct__mention">{match[1]}</span>
      {value.slice(match[1].length)}
    </>
  );
}

/* ──────────── Comment row ──────────── */

function CommentRow({
  comment,
  onLike,
  onDelete,
  onReplyClick,
  isReplyTarget = false,
  onUserClick,
  currentUserId,
  anchorPrefix,
}) {
  const isOwner = currentUserId != null
    && comment.userId != null
    && Number(currentUserId) === Number(comment.userId);

  // isLiked comes from the backend (comment.currentUserLiked) — we don't
  // need a local setLikedComments; here we just read it off the comment.
  const isLiked = Boolean(comment.currentUserLiked);

  // The avatar + name are clickable buttons (not <a> tags) so screen
  // readers announce them as actions and we don't get the default <a>
  // underline. `onUserClick` is wired by the Q&A page to navigate to
  // /profile/<username>. We only enable the buttons when both the handler
  // AND a username/userId are present — never the case for the placeholder
  // "Comentário do sistema" row that some legacy data may carry.
  const userClickable = Boolean(onUserClick && (comment.username || comment.userId));
  const authorLabel = comment.user || comment.username || 'Utilizador';
  const profileLabel = userClickable
    ? `Ver perfil de ${authorLabel}`
    : authorLabel;

  return (
    <motion.div
      className={`gm-ct__row ${comment.__depth > 0 ? 'gm-ct__row--reply' : ''}`}
      id={anchorPrefix && comment.id != null ? `${anchorPrefix}-${comment.id}` : undefined}
      data-comment-id={comment.id != null ? comment.id : undefined}
      style={{ '--gm-ct-depth': Math.min(comment.__depth || 0, 2) }}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
    >
      <button
        type="button"
        className={`gm-ct__avatar ${userClickable ? 'gm-ct__avatar--clickable' : ''}`}
        onClick={() => userClickable && onUserClick(comment)}
        disabled={!userClickable}
        aria-label={profileLabel}
      >
        <Avatar
          src={comment.userProfilePicture || comment.userPhoto || null}
          name={authorLabel}
          photoPosition={comment.userProfilePhotoPosition}
          // Round 85 — `size` only accepts the named sizes ('xs' | 'sm' |
          // 'md' | 'lg' | 'xl' | '2xl'). The previous `size={36}` (numeric)
          // silently fell back to the default 'md' (40px), which overflowed
          // the surrounding 36px button. 'sm' = 32px, padded by the
          // button to 36px.
          size="sm"
        />
      </button>

      <div className="gm-ct__body">
        <div className="gm-ct__head">
          {userClickable ? (
            <button
              type="button"
              className="gm-ct__name gm-ct__name--clickable"
              onClick={() => onUserClick(comment)}
            >
              {authorLabel}
            </button>
          ) : (
            <span className="gm-ct__name">{authorLabel}</span>
          )}
          <span className="gm-ct__time" title={formatDateTime(comment.createdAt)}>
            {timeAgo(comment.createdAt)}
          </span>
        </div>

        <p className="gm-ct__text">{renderCommentText(comment.text || comment.content)}</p>

        <div className="gm-ct__actions">
          <button
            type="button"
            className={`gm-ct__action ${isLiked ? 'gm-ct__action--liked' : ''}`}
            onClick={() => onLike?.(comment)}
            aria-label={isLiked ? 'Remover gosto' : 'Gostar'}
          >
            <Heart size={14} fill={isLiked ? 'currentColor' : 'none'} strokeWidth={2} />
            {comment.likes > 0 && <span>{comment.likes}</span>}
          </button>

          {onReplyClick && (
            <button
              type="button"
              className={`gm-ct__action gm-ct__action--reply ${isReplyTarget ? 'gm-ct__action--reply-active' : ''}`}
              onClick={onReplyClick}
              aria-label="Responder ao comentário"
            >
              <Reply size={14} strokeWidth={2} />
              <span>Responder</span>
            </button>
          )}

          {isOwner && (
            <button
              type="button"
              className="gm-ct__action gm-ct__action--danger"
              onClick={() => onDelete?.(comment)}
              aria-label="Eliminar comentário"
            >
              <Trash2 size={14} strokeWidth={2} />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ──────────── Public component ──────────── */

const CommentThread = ({
  // List of comments already flattened (use `flattenCommentTree` if you have
  // a tree from the backend).
  comments = [],
  totalCount = 0,
  isOpen = true,
  onToggle,
  composer, // { author: { name, src? }, onSubmit: async (text) => … }
  // Per-comment actions
  onLike, // (comment) => …
  onDelete, // (comment) => …
  onReply, // (comment, text) => …
  // Round 83 — (comment) => navigate to the author's profile. The Q&A page
  // wires this to /profile/<username>. We pass it down to every CommentRow
  // so the avatar and the name become buttons that fire the navigation.
  onUserClick,
  currentUserId,
  showCount = true,
  emptyText = 'Ainda sem comentários. Sê o primeiro a comentar!',
  loading = false,
  anchorPrefix = 'comment',
  focusCommentId = null,
  mobileSheet = false,
  mobileFooter = null,
  // Some pages, such as Q&A, need the DOM to preserve one wrapper per
  // root comment so the connector can end at the last reply in that tree.
  // The default stays flat for the feed and trip-detail consumers.
  threadedGroups = false,
}) => {
  const [replyingTo, setReplyingTo] = useState(null);
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [visibleRootCount, setVisibleRootCount] = useState(INITIAL_VISIBLE_ROOTS);
  const [visibleRepliesByRoot, setVisibleRepliesByRoot] = useState({});
  const [expandedReplyRoots, setExpandedReplyRoots] = useState({});
  const composerInputRef = useRef(null);

  const updateComposerValue = useCallback((value) => {
    composer?.onChange?.(String(value ?? '').slice(0, COMMENT_LIMITS.MAX_LENGTH));
  }, [composer]);
  const composerValue = String(composer?.value || '').slice(0, COMMENT_LIMITS.MAX_LENGTH);

  const openReply = useCallback((comment) => {
    const commentKey = getCommentKey(comment);
    if (replyingTo?.key === commentKey) {
      updateComposerValue('');
      setReplyingTo(null);
      return;
    }
    const handle = getMentionHandle(comment);
    updateComposerValue(handle ? `@${handle} ` : '');
    setReplyingTo({ key: commentKey, comment });
  }, [replyingTo, updateComposerValue]);

  useEffect(() => {
    if (!replyingTo) return;
    composerInputRef.current?.focus();
  }, [replyingTo]);

  useEffect(() => {
    if (!isOpen) setReplyingTo(null);
  }, [isOpen]);

  const cancelReply = useCallback(() => {
    updateComposerValue('');
    setReplyingTo(null);
  }, [updateComposerValue]);

  const submitComposer = useCallback(async () => {
    const text = composerValue.trim();
    if (!text || replySubmitting || composer?.disabled) return false;
    setReplySubmitting(true);
    try {
      const result = replyingTo && onReply
        ? await onReply(
          replyingTo.comment,
          withReplyMention(text, replyingTo.comment).slice(0, COMMENT_LIMITS.MAX_LENGTH).trim(),
        )
        : await composer?.onSubmit?.(text);
      if (result !== false) {
        updateComposerValue('');
        setReplyingTo(null);
      }
      return result !== false;
    } finally {
      setReplySubmitting(false);
    }
  }, [composer, composerValue, onReply, replySubmitting, replyingTo, updateComposerValue]);

  // Stable order: newest first by createdAt (fallback to original index).
  const ordered = useMemo(() => {
    const threaded = orderFlatThread(comments);
    if (threaded.some((comment) => comment?.parentId != null)) return threaded;
    return [...threaded].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : Number.NEGATIVE_INFINITY;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : Number.NEGATIVE_INFINITY;
      if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return tb - ta;
      return 0;
    });
  }, [comments]);

  // Build one group per root comment so that replies stay visually attached
  // to the comment they answer, even when the API returns a flat list.
  const threadGroups = useMemo(() => {
    const byId = new Map();
    ordered.forEach((comment) => {
      if (comment?.id != null) byId.set(String(comment.id), comment);
    });

    const rootFor = (comment) => {
      let current = comment;
      const visited = new Set();
      while (current && getParentId(current) != null) {
        const currentKey = getCommentKey(current);
        if (visited.has(currentKey)) break;
        visited.add(currentKey);
        const parent = byId.get(String(getParentId(current)));
        if (!parent) break;
        current = parent;
      }
      return current || comment;
    };

    const groups = new Map();
    ordered.forEach((comment) => {
      const root = rootFor(comment);
      const rootKey = getCommentKey(root);
      if (!groups.has(rootKey)) groups.set(rootKey, { key: rootKey, comments: [] });
      groups.get(rootKey).comments.push(comment);
    });
    return Array.from(groups.values());
  }, [ordered]);

  // Deep links from notifications should reveal the target even when it is
  // beyond the initial compact view (three roots / three replies). Keep the
  // normal "Ver mais comentários" behaviour for every other visit.
  useEffect(() => {
    if (focusCommentId == null || !threadGroups.length) return;
    const targetId = String(focusCommentId);
    const groupIndex = threadGroups.findIndex((group) => (
      group.comments.some((comment) => String(comment?.id) === targetId)
    ));
    if (groupIndex < 0) return;

    setVisibleRootCount((current) => Math.max(current, groupIndex + 1));
    const group = threadGroups[groupIndex];
    const targetIndex = group.comments.findIndex((comment) => String(comment?.id) === targetId);
    if (targetIndex > 0) {
      if (mobileSheet) {
        setExpandedReplyRoots((current) => ({ ...current, [group.key]: true }));
      }
      setVisibleRepliesByRoot((current) => {
        const currentLimit = current[group.key] ?? INITIAL_VISIBLE_REPLIES;
        if (currentLimit >= targetIndex) return current;
        return { ...current, [group.key]: targetIndex };
      });
    }
  }, [focusCommentId, mobileSheet, threadGroups]);

  const visibleComments = useMemo(() => (
    threadGroups
      .slice(0, visibleRootCount)
      .flatMap((group) => {
        const replyLimit = visibleRepliesByRoot[group.key] ?? INITIAL_VISIBLE_REPLIES;
        return group.comments.slice(0, 1 + replyLimit);
      })
  ), [threadGroups, visibleRepliesByRoot, visibleRootCount]);

  const hasMoreComments = threadGroups.length > visibleRootCount
    || threadGroups.slice(0, visibleRootCount).some((group) => (
      group.comments.length > 1 + (visibleRepliesByRoot[group.key] ?? INITIAL_VISIBLE_REPLIES)
    ));

  const showMoreComments = useCallback(() => {
    if (mobileSheet) {
      setVisibleRootCount((current) => Math.min(current + 3, threadGroups.length));
      return;
    }
    const groupWithMoreReplies = threadGroups
      .slice(0, visibleRootCount)
      .find((group) => (
        group.comments.length > 1 + (visibleRepliesByRoot[group.key] ?? INITIAL_VISIBLE_REPLIES)
      ));

    if (groupWithMoreReplies) {
      setVisibleRepliesByRoot((current) => ({
        ...current,
        [groupWithMoreReplies.key]: (current[groupWithMoreReplies.key] ?? INITIAL_VISIBLE_REPLIES) + 3,
      }));
      return;
    }
    setVisibleRootCount((current) => Math.min(current + 3, threadGroups.length));
  }, [mobileSheet, threadGroups, visibleRepliesByRoot, visibleRootCount]);

  const toggleReplyGroup = useCallback((groupKey) => {
    setExpandedReplyRoots((current) => ({
      ...current,
      [groupKey]: !current[groupKey],
    }));
  }, []);

  const focusComposer = useCallback(() => {
    composerInputRef.current?.focus();
  }, []);

  // A consumer can receive the comments in a separate request from the
  // aggregate count (the trip detail page does exactly that). Never show
  // "0 comentários" while the component already has visible comments.
  // Prefer the server total when it is available, but use the local list as
  // a safe lower bound while the count request catches up.
  const displayCount = Math.max(Number(totalCount) || 0, comments.length);

  return (
    <div className={`gm-ct ${mobileSheet ? 'gm-ct--mobile-sheet' : ''}${threadedGroups ? ' gm-ct--threaded-groups' : ''}`}>
      {mobileSheet ? (
        <header className="gm-ct__sheet-header">
          <span className="gm-ct__sheet-grabber" aria-hidden="true" />
          <h2 className="gm-ct__sheet-title">
            Comentários
            <span className="gm-ct__sheet-count">{displayCount}</span>
          </h2>
          <button
            type="button"
            className="gm-ct__sheet-close"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onToggle?.();
            }}
            aria-label="Fechar comentários"
          >
            <X size={22} strokeWidth={2} />
          </button>
        </header>
      ) : showCount && (
        <button
          type="button"
          className="gm-ct__head-toggle"
          onClick={() => onToggle?.()}
        >
          <span>
            {isOpen ? 'Esconder' : 'Ver'} {displayCount}{' '}
            {displayCount === 1 ? 'comentário' : 'comentários'}
          </span>
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      )}

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="comments-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="gm-ct__body-wrap"
          >
            {/* List: top-level comments and their replies share one visual
                system, with replies indented below their parent. */}
            {loading ? (
              <div className="gm-ct__loading">A carregar comentários…</div>
            ) : ordered.length === 0 ? (
              <div className={`gm-ct__empty ${mobileSheet ? 'gm-ct__empty--mobile' : ''}`}>
                {mobileSheet ? (
                  <>
                    <MessageCircle size={48} strokeWidth={1.5} aria-hidden="true" />
                    <p>Gostaste desta publicação? Sê a primeira pessoa a comentar.</p>
                    {composer && (
                      <button type="button" onClick={focusComposer}>Comentar</button>
                    )}
                  </>
                ) : emptyText}
              </div>
            ) : (
              <div className="gm-ct__list">
                {mobileSheet ? (
                  // The mobile sheet is scrollable, so do not hide the rest
                  // of the conversation behind the desktop three-item
                  // preview. Every fetched top-level comment is visible.
                  threadGroups.slice(0, mobileSheet ? threadGroups.length : visibleRootCount).map((group) => {
                    const root = group.comments[0];
                    const rootKey = getCommentKey(root);
                    const replies = group.comments.slice(1);
                    const isExpanded = Boolean(expandedReplyRoots[group.key]);
                    const replyLimit = visibleRepliesByRoot[group.key] ?? INITIAL_VISIBLE_REPLIES;
                    const visibleReplies = isExpanded ? replies.slice(0, replyLimit) : [];
                    const hasMoreReplies = isExpanded && replies.length > visibleReplies.length;
                    return (
                      <div className="gm-ct__thread-group" key={group.key}>
                        <CommentRow
                          comment={root}
                          onLike={onLike}
                          onDelete={onDelete}
                          onReplyClick={onReply && composer ? () => openReply(root) : undefined}
                          isReplyTarget={replyingTo?.key === rootKey}
                          onUserClick={onUserClick}
                          currentUserId={currentUserId}
                          anchorPrefix={anchorPrefix}
                        />
                        {replies.length > 0 && (
                          <button
                            type="button"
                            className="gm-ct__replies-toggle"
                            onClick={() => toggleReplyGroup(group.key)}
                            aria-expanded={isExpanded}
                          >
                            <span>
                              {isExpanded
                                ? 'Ocultar respostas'
                                : `Ver ${replies.length} ${replies.length === 1 ? 'resposta' : 'respostas'}`}
                            </span>
                            {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                          </button>
                        )}
                        {visibleReplies.map((reply) => {
                          const replyKey = getCommentKey(reply);
                          return (
                            <CommentRow
                              key={replyKey}
                              comment={reply}
                              onLike={onLike}
                              onDelete={onDelete}
                              onReplyClick={onReply && composer ? () => openReply(reply) : undefined}
                              isReplyTarget={replyingTo?.key === replyKey}
                              onUserClick={onUserClick}
                              currentUserId={currentUserId}
                              anchorPrefix={anchorPrefix}
                            />
                          );
                        })}
                        {hasMoreReplies && (
                          <button
                            type="button"
                            className="gm-ct__replies-more"
                            onClick={() => setVisibleRepliesByRoot((current) => ({
                              ...current,
                              [group.key]: replyLimit + 3,
                            }))}
                          >
                            Ver mais respostas
                          </button>
                        )}
                      </div>
                    );
                  })
                ) : threadedGroups ? (
                  threadGroups.slice(0, visibleRootCount).map((group) => {
                    const replyLimit = visibleRepliesByRoot[group.key] ?? INITIAL_VISIBLE_REPLIES;
                    return (
                      <div className="gm-ct__thread-group" key={group.key}>
                        {group.comments.slice(0, 1 + replyLimit).map((comment) => {
                          const commentKey = getCommentKey(comment);
                          return (
                            <CommentRow
                              key={commentKey}
                              comment={comment}
                              onLike={onLike}
                              onDelete={onDelete}
                              onReplyClick={onReply && composer ? () => openReply(comment) : undefined}
                              isReplyTarget={replyingTo?.key === commentKey}
                              onUserClick={onUserClick}
                              currentUserId={currentUserId}
                              anchorPrefix={anchorPrefix}
                            />
                          );
                        })}
                      </div>
                    );
                  })
                ) : visibleComments.map((c) => {
                  const commentKey = getCommentKey(c);
                  return (
                    <React.Fragment key={commentKey}>
                      <CommentRow
                        comment={c}
                        onLike={onLike}
                        onDelete={onDelete}
                        onReplyClick={onReply && composer ? () => openReply(c) : undefined}
                        isReplyTarget={replyingTo?.key === commentKey}
                        onUserClick={onUserClick}
                        currentUserId={currentUserId}
                        anchorPrefix={anchorPrefix}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {!mobileSheet && hasMoreComments && (
              <button
                type="button"
                className="gm-ct__load-more"
                onClick={showMoreComments}
              >
                Ver mais comentários
              </button>
            )}

            {mobileSheet && mobileFooter && (
              <div className="gm-ct__mobile-footer">{mobileFooter}</div>
            )}

            {composer && (
              <div className="gm-ct__composer">
                <Avatar
                  src={composer.author?.src || null}
                  name={composer.author?.name || 'Você'}
                  photoPosition={composer.author?.photoPosition}
                  // Round 85 — same fix as the row avatar: use the named
                  // 'sm' (32px) instead of the numeric 32 (which silently
                  // defaulted to 'md' = 40px and overflowed the
                  // composer row).
                  size="sm"
                />
                <div className="gm-ct__composer-main">
                  {(
                    <div className="gm-ct__quick-reactions" aria-label="Reações rápidas">
                      {QUICK_REACTIONS.map((reaction) => (
                        <button
                          key={reaction}
                          type="button"
                          className="gm-ct__quick-reaction"
                          onClick={() => updateComposerValue(`${composerValue}${reaction}`)}
                          aria-label={`Adicionar ${reaction}`}
                        >
                          {reaction}
                        </button>
                      ))}
                    </div>
                  )}
                  {replyingTo && (
                    <div className="gm-ct__composer-context" aria-live="polite">
                      <span>
                        <Reply size={13} strokeWidth={2} />
                        A responder a{' '}
                        <strong>@{getMentionHandle(replyingTo.comment) || 'este comentário'}</strong>
                      </span>
                      <button
                        type="button"
                        className="gm-ct__composer-cancel"
                        onClick={cancelReply}
                        aria-label="Cancelar resposta"
                      >
                        <X size={14} strokeWidth={2} />
                      </button>
                    </div>
                  )}
                  <form
                    className="gm-ct__composer-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void submitComposer();
                    }}
                  >
                    <input
                      ref={composerInputRef}
                      type="text"
                      className="gm-ct__composer-input"
                      placeholder={mobileSheet ? 'Adicionar comentário...' : (composer.placeholder || 'Escreva um comentário...')}
                      value={composerValue}
                      maxLength={COMMENT_LIMITS.MAX_LENGTH}
                      onChange={(e) => updateComposerValue(e.target.value)}
                      disabled={composer.disabled || replySubmitting}
                      aria-label={replyingTo
                        ? `Responder a ${replyingTo.comment?.user || replyingTo.comment?.username || 'este comentário'}`
                        : 'Escreva um comentário'}
                    />
                    <button
                      type="submit"
                      className="gm-ct__composer-submit"
                      disabled={!composerValue.trim() || composer.disabled || replySubmitting}
                      aria-label={replyingTo ? 'Enviar resposta' : 'Publicar comentário'}
                    >
                      <Send size={16} strokeWidth={2.2} />
                    </button>
                  </form>
                  <div className="gm-ct__composer-meta" aria-live="polite">
                    <span>Máximo {COMMENT_LIMITS.MAX_LENGTH} caracteres.</span>
                    <span>{composerValue.length}/{COMMENT_LIMITS.MAX_LENGTH}</span>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

CommentThread.propTypes = {
  comments: PropTypes.array,
  totalCount: PropTypes.number,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  composer: PropTypes.shape({
    author: PropTypes.shape({ name: PropTypes.string, src: PropTypes.string }),
    onSubmit: PropTypes.func,
    onChange: PropTypes.func,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
  }),
  onLike: PropTypes.func,
  onDelete: PropTypes.func,
  onReply: PropTypes.func,
  // Round 83 — wire from the Q&A page to navigate to the author's profile.
  onUserClick: PropTypes.func,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showCount: PropTypes.bool,
  emptyText: PropTypes.string,
  loading: PropTypes.bool,
  anchorPrefix: PropTypes.string,
  focusCommentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mobileSheet: PropTypes.bool,
  mobileFooter: PropTypes.node,
  threadedGroups: PropTypes.bool,
};

export default CommentThread;
